import { type DocumentData, type DocumentSnapshot, collection, doc } from 'firebase/firestore'

import { callable } from '@/lib/firebase/callable'
import { type Subscription, subscribeCollection } from '@/lib/firebase/subscribe'
import {
  type SetupScope,
  asIsoOrNull,
  asNumber,
  asOneOf,
  asRecord,
  asRecords,
  asString,
  asStringOrNull,
  asStrings,
  isRecord,
  setupCollection,
  subscribeAll,
  subscribeLatestRun,
  subscribeOne,
  toSetupRun,
  toStageEntryBase,
} from '@/repositories/setup-docs'
import { JOURNEY_PHASES } from '@/types/brand'
import {
  COMPETITION_LEVELS,
  INVENTORY_PAGE_SOURCES,
  PAGE_KEYWORD_ROLES,
  PLAN_RUN_ERROR_CODES,
  PLAN_RUN_STAGES,
  SITE_MATCHES,
  PAGE_CHANGE_FIELDS,
  type CommitCounts,
  type CompetitorsCounts,
  type ContentCounts,
  type DecisionPagesCounts,
  type DemandCounts,
  type FillCounts,
  type GroupingCounts,
  type InventoryCounts,
  type InventoryPage,
  type InventoryPageSource,
  type InventoryRanking,
  type KeywordMetrics,
  type KeywordRanks,
  type Page,
  type PageChangeField,
  type PageKeyword,
  type PlacementCounts,
  type PlanDiff,
  type PlanDiffPage,
  type PlanDiffTopic,
  type PlanEdits,
  type PlanEditStage,
  type PlanFocus,
  type PlanGateReply,
  type PlanReviews,
  type PlanRun,
  type PlanRunStage,
  type PlanRunStageEntry,
  type PlanStepEditsInput,
  type RankPoint,
  type RelevanceCounts,
  type ResearchCounts,
  type ReviewPlanCounts,
  type ReviewTopicsCounts,
  type SerpCounts,
  type SiteInventory,
  type SiteMatchCounts,
  type StartPlanInput,
  type TopicCandidatesCounts,
  type Topic,
  type TypeStageCounts,
  PAGE_TYPES,
} from '@/types/plan'
import { toPlanStageReview, toPlanStepEdits } from '@/repositories/plan-review'
import { SEARCH_INTENTS, type SearchIntent } from '@/types/search-intent'
import type { RunStatus } from '@/types/setup-run'

function asFiniteOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function toInventoryCounts(value: unknown): InventoryCounts {
  const counts = asRecord(value)
  return {
    pages: asNumber(counts.pages),
    fromSitemap: asNumber(counts.fromSitemap),
    ranking: asNumber(counts.ranking),
    readable: asNumber(counts.readable),
    gone: asNumber(counts.gone),
  }
}

function toContentCounts(value: unknown): ContentCounts {
  const counts = asRecord(value)
  return {
    pages: asNumber(counts.pages),
    read: asNumber(counts.read),
    unchanged: asNumber(counts.unchanged),
  }
}

function toResearchCounts(value: unknown): ResearchCounts {
  const counts = asRecord(value)
  return { coverage: asNumber(counts.coverage), terms: asNumber(counts.terms) }
}

function toCompetitorsCounts(value: unknown): CompetitorsCounts {
  const counts = asRecord(value)
  return { competitors: asNumber(counts.competitors), searches: asNumber(counts.searches) }
}

function toDemandCounts(value: unknown): DemandCounts {
  const counts = asRecord(value)
  return {
    terms: asNumber(counts.terms),
    measured: asNumber(counts.measured),
    units: asNumber(counts.units),
  }
}

function toTopicCandidatesCounts(value: unknown): TopicCandidatesCounts {
  return { candidates: asNumber(asRecord(value).candidates) }
}

function toReviewTopicsCounts(value: unknown): ReviewTopicsCounts {
  const counts = asRecord(value)
  return { kept: asNumber(counts.kept), added: asNumber(counts.added) }
}

function toFillCounts(value: unknown): FillCounts {
  const counts = asRecord(value)
  return { topics: asNumber(counts.topics), units: asNumber(counts.units) }
}

function toPlacementCounts(value: unknown): PlacementCounts {
  const counts = asRecord(value)
  return {
    pages: asNumber(counts.pages),
    topics: asNumber(counts.topics),
    dropped: asNumber(counts.dropped),
  }
}

function toReviewPlanCounts(value: unknown): ReviewPlanCounts {
  return { removed: asNumber(asRecord(value).removed) }
}

function toDecisionPagesCounts(value: unknown): DecisionPagesCounts {
  const counts = asRecord(value)
  return { pages: asNumber(counts.pages), objectives: asNumber(counts.objectives) }
}

function toSiteMatchCounts(value: unknown): SiteMatchCounts {
  const counts = asRecord(value)
  return {
    planned: asNumber(counts.planned),
    existing: asNumber(counts.existing),
    excluded: asNumber(counts.excluded),
    byRanking: asNumber(counts.byRanking),
    byModel: asNumber(counts.byModel),
    update: asNumber(counts.update),
  }
}

function toCommitCounts(value: unknown): CommitCounts {
  const counts = asRecord(value)
  return {
    topics: asNumber(counts.topics),
    pages: asNumber(counts.pages),
    keywords: asNumber(counts.keywords),
    removed: asNumber(counts.removed),
  }
}

function toRelevanceCounts(value: unknown): RelevanceCounts {
  const counts = asRecord(value)
  return {
    units: asNumber(counts.units),
    scored: asNumber(counts.scored),
    kept: asNumber(counts.kept),
  }
}

function toSerpCounts(value: unknown): SerpCounts {
  const counts = asRecord(value)
  return {
    keywords: asNumber(counts.keywords),
    ordered: asNumber(counts.ordered),
    received: asNumber(counts.received),
    missing: asNumber(counts.missing),
    clusters: asNumber(counts.clusters),
  }
}

function toGroupingCounts(value: unknown): GroupingCounts {
  const counts = asRecord(value)
  return { keywords: asNumber(counts.keywords), pages: asNumber(counts.pages) }
}

function toTypeStageCounts(value: unknown): TypeStageCounts {
  const counts = asRecord(value)
  return {
    pages: asNumber(counts.pages),
    kept: asNumber(counts.kept),
    dropped: asNumber(counts.dropped),
  }
}

function toStageEntry(entry: DocumentData): PlanRunStageEntry | undefined {
  const base = toStageEntryBase(entry)
  const { counts } = entry
  switch (entry.id as PlanRunStage) {
    case 'inventory':
      return { ...base, id: 'inventory', counts: toInventoryCounts(counts) }
    case 'content':
      return { ...base, id: 'content', counts: toContentCounts(counts) }
    case 'research':
      return { ...base, id: 'research', counts: toResearchCounts(counts) }
    case 'competitors':
      return { ...base, id: 'competitors', counts: toCompetitorsCounts(counts) }
    case 'demand':
      return { ...base, id: 'demand', counts: toDemandCounts(counts) }
    case 'relevance':
      return { ...base, id: 'relevance', counts: toRelevanceCounts(counts) }
    case 'topic-candidates':
      return { ...base, id: 'topic-candidates', counts: toTopicCandidatesCounts(counts) }
    case 'review-topics':
      return { ...base, id: 'review-topics', counts: toReviewTopicsCounts(counts) }
    case 'fill':
      return { ...base, id: 'fill', counts: toFillCounts(counts) }
    case 'serp':
      return { ...base, id: 'serp', counts: toSerpCounts(counts) }
    case 'grouping':
      return { ...base, id: 'grouping', counts: toGroupingCounts(counts) }
    case 'type-stage':
      return { ...base, id: 'type-stage', counts: toTypeStageCounts(counts) }
    case 'placement':
      return { ...base, id: 'placement', counts: toPlacementCounts(counts) }
    case 'decision-pages':
      return { ...base, id: 'decision-pages', counts: toDecisionPagesCounts(counts) }
    case 'site-match':
      return { ...base, id: 'site-match', counts: toSiteMatchCounts(counts) }
    case 'review-plan':
      return { ...base, id: 'review-plan', counts: toReviewPlanCounts(counts) }
    case 'commit':
      return { ...base, id: 'commit', counts: toCommitCounts(counts) }
    default:
      return undefined
  }
}

function toDiffTopic(data: DocumentData): PlanDiffTopic {
  return { id: asString(data.id), name: asString(data.name) }
}

function toDiffPage(data: DocumentData): PlanDiffPage {
  return { id: asString(data.id), question: asString(data.question), topic: asString(data.topic) }
}

function isPageChangeField(value: string): value is PageChangeField {
  return (PAGE_CHANGE_FIELDS as readonly string[]).includes(value)
}

/** Maps a plan run's `diff`; `null` when the run proposes nothing, an unknown changed field is dropped. */
export function toPlanDiff(value: unknown): PlanDiff | null {
  if (!isRecord(value)) return null
  const topics = asRecord(value.topics)
  const pages = asRecord(value.pages)
  const keywords = asRecord(value.keywords)
  return {
    topics: {
      added: asRecords(topics.added).map(toDiffTopic),
      removed: asRecords(topics.removed).map(toDiffTopic),
      renamed: asRecords(topics.renamed).map((topic) => ({
        ...toDiffTopic(topic),
        from: asString(topic.from),
      })),
    },
    pages: {
      added: asRecords(pages.added).map(toDiffPage),
      removed: asRecords(pages.removed).map(toDiffPage),
      changed: asRecords(pages.changed).map((page) => ({
        ...toDiffPage(page),
        fields: asStrings(page.fields).filter(isPageChangeField),
      })),
      kept: asRecords(pages.kept).map(toDiffPage),
    },
    keywords: { added: asNumber(keywords.added), removed: asNumber(keywords.removed) },
  }
}

function toPlanFocus(value: unknown): PlanFocus | null {
  return isRecord(value)
    ? { topicId: asString(value.topicId), subject: asString(value.subject) }
    : null
}

/** Maps a `planRuns` doc; an entry of an unknown stage is dropped, an unknown error code reads as `plan_run_failed`. */
export function toPlanRun(id: string, data: DocumentData): PlanRun {
  const { errorCode, ...run } = toSetupRun(id, data, PLAN_RUN_STAGES, toStageEntry)
  const market = asRecord(data.market)
  return {
    ...run,
    ...(errorCode === undefined
      ? {}
      : { errorCode: asOneOf(errorCode, PLAN_RUN_ERROR_CODES, 'plan_run_failed') }),
    startedByUserId: asString(data.startedByUserId),
    strategyStamp: asIsoOrNull(data.strategyStamp),
    domain: asString(data.domain),
    market: {
      locationCode: asNumber(market.locationCode),
      languageCode: asString(market.languageCode),
    },
    focus: toPlanFocus(data.focus),
    direction: asStringOrNull(data.direction),
    remindedAt: asStringOrNull(data.remindedAt),
    diff: toPlanDiff(data.diff),
  }
}

/** Maps the `siteInventory/current` doc. */
export function toSiteInventory(data: DocumentData): SiteInventory {
  const robots = asRecord(data.robots)
  const rankedKeywords = asRecord(data.rankedKeywords)
  return {
    domain: asString(data.domain),
    fetchedAt: asIsoOrNull(data.fetchedAt),
    robots: {
      status: asFiniteOrNull(robots.status),
      disallow: asStrings(robots.disallow),
      sitemapUrls: asStrings(robots.sitemapUrls),
    },
    sitemaps: asStrings(data.sitemaps),
    domainRank: asFiniteOrNull(data.domainRank),
    rankedKeywords: {
      total: asNumber(rankedKeywords.total),
      saved: asNumber(rankedKeywords.saved),
      etv: asFiniteOrNull(rankedKeywords.etv),
    },
    counts: toInventoryCounts(data.counts),
    contentReadAt: asIsoOrNull(data.contentReadAt),
  }
}

function toRanking(value: DocumentData): InventoryRanking | undefined {
  const keyword = asString(value.keyword)
  if (keyword === '') return undefined
  return {
    keyword,
    position: asFiniteOrNull(value.position),
    volume: asFiniteOrNull(value.volume),
    difficulty: asFiniteOrNull(value.difficulty),
    etv: asFiniteOrNull(value.etv),
  }
}

/** Maps an `inventoryPages` doc; `undefined` without a url. */
export function toInventoryPage(id: string, data: DocumentData): InventoryPage | undefined {
  const url = asString(data.url)
  if (url === '') return undefined
  return {
    id,
    url,
    sources: asStrings(data.sources).filter((source): source is InventoryPageSource =>
      (INVENTORY_PAGE_SOURCES as readonly string[]).includes(source),
    ),
    lastmod: asIsoOrNull(data.lastmod),
    status: asFiniteOrNull(data.status),
    redirectsTo: asStringOrNull(data.redirectsTo),
    robotsAllowed: data.robotsAllowed !== false,
    noindex: data.noindex === true,
    canonical: asStringOrNull(data.canonical),
    title: asStringOrNull(data.title),
    description: asStringOrNull(data.description),
    h1: asStringOrNull(data.h1),
    h2s: asStrings(data.h2s),
    words: asNumber(data.words),
    rankings: asRecords(data.rankings)
      .map(toRanking)
      .filter((ranking): ranking is InventoryRanking => ranking !== undefined),
    firstSeenAt: asIsoOrNull(data.firstSeenAt),
    lastSeenAt: asIsoOrNull(data.lastSeenAt),
    goneAt: asIsoOrNull(data.goneAt),
  }
}

/** Maps a `topics` doc. */
export function toTopic(id: string, data: DocumentData): Topic {
  return {
    id,
    brandId: asString(data.brandId),
    name: asString(data.name),
    whatItCovers: asString(data.whatItCovers),
    hubPageId: asString(data.hubPageId),
    beliefSlugs: asStrings(data.beliefSlugs),
    position: asNumber(data.position),
    createdAt: asIsoOrNull(data.createdAt),
    updatedAt: asIsoOrNull(data.updatedAt),
  }
}

function toExisting(value: unknown): Page['existing'] {
  if (!isRecord(value)) return null
  const inventoryPageId = asString(value.inventoryPageId)
  if (inventoryPageId === '') return null
  return {
    inventoryPageId,
    match: asOneOf(value.match, SITE_MATCHES, 'partly'),
    reason: asString(value.reason),
  }
}

function toObjective(value: unknown): Page['objective'] {
  if (!isRecord(value)) return null
  return {
    beliefSlug: asStringOrNull(value.beliefSlug),
    competitor: asStringOrNull(value.competitor),
  }
}

function toLeadsToOffer(value: unknown): Page['leadsToOffer'] {
  return value === 1 || value === 2 || value === 3 ? value : 1
}

/** Maps a `pages` doc. */
export function toPage(id: string, data: DocumentData): Page {
  return {
    id,
    brandId: asString(data.brandId),
    topicId: asString(data.topicId),
    question: asString(data.question),
    type: asOneOf(data.type, PAGE_TYPES, 'supporting'),
    stage: asOneOf(data.stage, JOURNEY_PHASES, 'awareness'),
    leadsToOffer: toLeadsToOffer(data.leadsToOffer),
    reason: asString(data.reason),
    writingOrder: asNumber(data.writingOrder),
    existing: toExisting(data.existing),
    objective: toObjective(data.objective),
    createdAt: asIsoOrNull(data.createdAt),
    updatedAt: asIsoOrNull(data.updatedAt),
  }
}

function asIntentOrNull(value: unknown): SearchIntent | null {
  return SEARCH_INTENTS.find((intent) => intent === value) ?? null
}

function toMetrics(value: unknown): KeywordMetrics {
  const metrics = asRecord(value)
  const trend = asRecord(metrics.trend)
  const bid = asRecord(metrics.topOfPageBid)
  const intent = asRecord(metrics.intent)
  return {
    measuredAt: asIsoOrNull(metrics.measuredAt),
    searchVolume: asFiniteOrNull(metrics.searchVolume),
    monthlySearches: asRecords(metrics.monthlySearches).flatMap((entry) => {
      const month = asString(entry.month)
      return month === '' ? [] : [{ month, volume: asNumber(entry.volume) }]
    }),
    trend: {
      monthly: asFiniteOrNull(trend.monthly),
      quarterly: asFiniteOrNull(trend.quarterly),
      yearly: asFiniteOrNull(trend.yearly),
    },
    cpc: asFiniteOrNull(metrics.cpc),
    competition: asFiniteOrNull(metrics.competition),
    competitionLevel:
      COMPETITION_LEVELS.find((level) => level === metrics.competitionLevel) ?? null,
    topOfPageBid: { low: asFiniteOrNull(bid.low), high: asFiniteOrNull(bid.high) },
    difficulty: asFiniteOrNull(metrics.difficulty),
    intent: {
      main: asIntentOrNull(intent.main),
      foreign: (Array.isArray(intent.foreign) ? intent.foreign : [])
        .map(asIntentOrNull)
        .filter((entry): entry is SearchIntent => entry !== null),
    },
    coreKeyword: asStringOrNull(metrics.coreKeyword),
    wordsCount: asFiniteOrNull(metrics.wordsCount),
    detectedLanguage: asStringOrNull(metrics.detectedLanguage),
    categories: (Array.isArray(metrics.categories) ? metrics.categories : []).filter(
      (entry): entry is number => typeof entry === 'number' && Number.isFinite(entry),
    ),
    serp: isRecord(metrics.serp)
      ? {
          resultsCount: asFiniteOrNull(metrics.serp.resultsCount),
          itemTypes: asStrings(metrics.serp.itemTypes),
        }
      : null,
    avgBacklinks: isRecord(metrics.avgBacklinks)
      ? {
          backlinks: asFiniteOrNull(metrics.avgBacklinks.backlinks),
          referringDomains: asFiniteOrNull(metrics.avgBacklinks.referringDomains),
          rank: asFiniteOrNull(metrics.avgBacklinks.rank),
        }
      : null,
  }
}

/** Maps a `pageKeywords` doc; `undefined` without a keyword. */
export function toPageKeyword(id: string, data: DocumentData): PageKeyword | undefined {
  const keyword = asString(data.keyword)
  if (keyword === '') return undefined
  return {
    id,
    brandId: asString(data.brandId),
    pageId: asString(data.pageId),
    keyword,
    role: asOneOf(data.role, PAGE_KEYWORD_ROLES, 'secondary'),
    variants: asStrings(data.variants),
    metrics: toMetrics(data.metrics),
    createdAt: asIsoOrNull(data.createdAt),
    updatedAt: asIsoOrNull(data.updatedAt),
  }
}

function toRankPoint(value: DocumentData): RankPoint | undefined {
  const week = asString(value.week)
  if (week === '') return undefined
  return { week, position: asFiniteOrNull(value.position), url: asStringOrNull(value.url) }
}

/** Maps a `keywordRanks` doc. */
export function toKeywordRanks(id: string, data: DocumentData): KeywordRanks {
  return {
    keywordId: asString(data.keywordId) || id,
    brandId: asString(data.brandId),
    points: asRecords(data.points)
      .map(toRankPoint)
      .filter((point): point is RankPoint => point !== undefined),
    updatedAt: asIsoOrNull(data.updatedAt),
  }
}

function toPlanRunSnapshot(snapshot: DocumentSnapshot<DocumentData>): PlanRun | undefined {
  const data = snapshot.data()
  return data ? toPlanRun(snapshot.id, data) : undefined
}

/** Live view of the brand's most recent plan run; `null` before the first. */
export function subscribeLatestPlanRun(scope: SetupScope): Subscription<PlanRun | null> {
  return subscribeLatestRun(scope, 'planRuns', toPlanRunSnapshot)
}

/** Live view of one plan run by id; absent when the run does not exist. */
export function subscribePlanRun(scope: SetupScope, runId: string): Subscription<PlanRun> {
  return subscribeOne(scope, 'planRuns', runId, toPlanRun)
}

function runSubcollection(scope: SetupScope, runId: string, name: string) {
  return collection(doc(setupCollection(scope, 'planRuns'), runId), name)
}

/** Live view of the review docs of one plan run, by stage. */
export function subscribePlanReviews(scope: SetupScope, runId: string): Subscription<PlanReviews> {
  return subscribeCollection(runSubcollection(scope, runId, 'review'), (snapshot) => {
    const reviews: { -readonly [S in keyof PlanReviews]: PlanReviews[S] } = {}
    for (const docSnapshot of snapshot.docs) {
      const review = toPlanStageReview(docSnapshot.id, docSnapshot.data())
      if (review) Object.assign(reviews, { [review.stage]: review })
    }
    return reviews
  })
}

/** Live view of the edits docs of one plan run, by stage. */
export function subscribePlanEdits(scope: SetupScope, runId: string): Subscription<PlanEdits> {
  return subscribeCollection(runSubcollection(scope, runId, 'edits'), (snapshot) => {
    const edits: { -readonly [S in keyof PlanEdits]: PlanEdits[S] } = {}
    for (const docSnapshot of snapshot.docs) {
      const stepEdits = toPlanStepEdits(docSnapshot.id, docSnapshot.data())
      if (stepEdits) Object.assign(edits, { [stepEdits.stage]: stepEdits })
    }
    return edits
  })
}

/** Live view of the brand's most recent completed plan run, the one the plan comes from; `null` before the first. */
export function subscribeLatestCompletedPlanRun(scope: SetupScope): Subscription<PlanRun | null> {
  return subscribeLatestRun(scope, 'planRuns', toPlanRunSnapshot, 'completed')
}

/** Live view of the brand's site inventory; absent before the first inventory. */
export function subscribeSiteInventory(scope: SetupScope): Subscription<SiteInventory> {
  return subscribeOne(scope, 'siteInventory', 'current', (_id, data) => toSiteInventory(data))
}

/** Live view of every page the inventory found. */
export function subscribeInventoryPages(scope: SetupScope): Subscription<InventoryPage[]> {
  return subscribeAll(scope, 'inventoryPages', toInventoryPage)
}

/** Live view of the plan's topics. */
export function subscribeTopics(scope: SetupScope): Subscription<Topic[]> {
  return subscribeAll(scope, 'topics', toTopic)
}

/** Live view of the plan's pages. */
export function subscribePages(scope: SetupScope): Subscription<Page[]> {
  return subscribeAll(scope, 'pages', toPage)
}

/** Live view of every keyword the plan's pages target. */
export function subscribePageKeywords(scope: SetupScope): Subscription<PageKeyword[]> {
  return subscribeAll(scope, 'pageKeywords', toPageKeyword)
}

/** Live view of the weekly rank history of every page keyword measured so far. */
export function subscribeKeywordRanks(scope: SetupScope): Subscription<KeywordRanks[]> {
  return subscribeAll(scope, 'keywordRanks', toKeywordRanks)
}

export interface StartPlanRunResult {
  runId: string
}

/** A plan run's status after a call that ends or settles it. */
export interface PlanRunStatusResult {
  status: RunStatus
}

const startPlanRunCallable = callable<StartPlanInput, StartPlanRunResult>('startPlanRun')

const cancelPlanRunCallable = callable<{ runId: string }, PlanRunStatusResult>('cancelPlanRun')

/** An answer to the gate of one run. */
export type PlanGateAnswerInput = PlanGateReply & { runId: string }

export interface RerunPlanFromInput {
  runId: string
  stage: PlanRunStage
}

export interface EditPlanStepInput {
  runId: string
  stage: PlanEditStage
  edits: PlanStepEditsInput
}

const answerPlanGateCallable = callable<PlanGateAnswerInput, PlanRunStatusResult>('answerPlanGate')

const rerunPlanFromCallable = callable<RerunPlanFromInput, StartPlanRunResult>('rerunPlanFrom')

const editPlanStepCallable = callable<EditPlanStepInput, PlanRunStatusResult>('editPlanStep')

const discardPlanProposalCallable = callable<{ runId: string }, PlanRunStatusResult>(
  'discardPlanProposal',
)

/** A page to add by hand to a topic: the question it answers and, optionally, the keyword to measure for it. */
export interface AddPageInput {
  topicId: string
  question: string
  keyword?: string
}

export interface AddPageResult {
  pageId: string
}

const addPageCallable = callable<AddPageInput, AddPageResult>('addPage', { timeout: 130_000 })

/** Opens a plan run for the caller's brand, or one that finds more pages for `focusTopicId`, steered by `direction` and measured in `market` when given; resolves the run id. */
export async function startPlanRun(input: StartPlanInput = {}): Promise<StartPlanRunResult> {
  return startPlanRunCallable({
    ...(input.focusTopicId === undefined ? {} : { focusTopicId: input.focusTopicId }),
    ...(input.direction === undefined ? {} : { direction: input.direction }),
    ...(input.market === undefined
      ? {}
      : {
          market: {
            locationCode: input.market.locationCode,
            languageCode: input.market.languageCode,
          },
        }),
  })
}

/** Cancels a live plan run; resolves the run's status after the call. */
export async function cancelPlanRun(runId: string): Promise<PlanRunStatusResult> {
  return cancelPlanRunCallable({ runId })
}

/** Answers the gate a plan run waits at; the run resumes. Resolves the run's status after the call. */
export async function answerPlanGate(input: PlanGateAnswerInput): Promise<PlanRunStatusResult> {
  return answerPlanGateCallable(input)
}

/** Opens a new plan run that keeps every stage before `stage` of the source run and starts at `stage`; resolves the new run id. */
export async function rerunPlanFrom(input: RerunPlanFromInput): Promise<StartPlanRunResult> {
  return rerunPlanFromCallable(input)
}

/** Saves what a person changed in one stage of a plan run; the stage applies it when it runs. */
export async function editPlanStep(input: EditPlanStepInput): Promise<PlanRunStatusResult> {
  return editPlanStepCallable(input)
}

/** Ends a waiting plan run without writing its proposal; resolves the run's status after the call. */
export async function discardPlanProposal(runId: string): Promise<PlanRunStatusResult> {
  return discardPlanProposalCallable({ runId })
}

/** Adds one page by hand at the end of a topic's writing order; resolves the new page's id. */
export async function addPage(input: AddPageInput): Promise<AddPageResult> {
  return addPageCallable(input)
}
